import {
  Box,
  Paper,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../../features/userSlice";
import {
  setSelectedSTOperationAccount,
  setSelectedSTOperationSharedUid,
} from "../../../../../features/searchTermOperationSlice";
import { useDispatch } from "react-redux";
import { useScopes } from "../STOperationhooks/useScopes";
import { useSearchParams } from "react-router-dom";

const SearchTermOperationCard = () => {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedSTOperationSharedUid,
    accountsForSTOperation,
    selectedSTOperationAccount,
    isScopesLoading,
    newScopeLoading,
    campaignToScopeLoading,
    editScopeLoading,
    removeNegativeLoadingForBothLoading,
    removeNegativeLoadingCampaignLoading,
  } = useSelector((state: any) => state.searchTermOperation);
  const {
    clearParams,
    clearParamsForAccountSelection,
    clearParamsOfTables,
    accountsLoading,
    getAccountsBasedOnSite,
    fetchScopesTabData,
  } = useScopes();

  // Function to handle changes in site selection
  const handleSitesChange = (_: unknown, newValue: any) => {
    if (newValue?.shared_uid !== selectedSTOperationSharedUid) {
      clearParams(); // Clear parameters and related states
      if (newValue) {
        dispatch(setSelectedSTOperationSharedUid(newValue.shared_uid));
        getAccountsBasedOnSite(newValue.shared_uid); // Fetch accounts for the selected site
      } else {
        dispatch(setSelectedSTOperationSharedUid(""));
      }
    }
  };

  // Function to handle "GET DATA" button click
  const handleGetDataSTO = () => {
    clearParamsOfTables(); // Clear parameters related to tables
    //set url params
    const params = new URLSearchParams(searchParams);
    params.set("selectedSite", selectedSTOperationSharedUid);
    params.set("account", selectedSTOperationAccount);
    params.set("clicked", "true");
    if (params.has("filters")) params.delete("filters");
    if (params.has("page2")) params.delete("page2");
    if (params.has("page3")) params.delete("page3");
    setSearchParams(params, { replace: true });

    //make api call
    fetchScopesTabData(
      selectedSTOperationSharedUid,
      selectedSTOperationAccount
    );
  };

  //-- Get website list those have permisssions
  const siteOptions = useMemo(() => {
    return (
      response?.data?.website_permissions?.map((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0]; // shared_uid of the site
        const site_name = siteObj[shared_uid].site_name; // Site name
        return { shared_uid, site_name }; // Return as an object with shared_uid and site_name
      }) || []
    );
  }, [response]);

  // Restore state from URL parameters on component mount(when reloaded page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const website = params.get("selectedSite");
    const account = params.get("account");
    const clicked = params.get("clicked");

    if (website) {
      dispatch(setSelectedSTOperationSharedUid(website)); // restore website shared_uid
      getAccountsBasedOnSite(website); // get account list based on website
    }
    if (account) dispatch(setSelectedSTOperationAccount(account));
    if (clicked === "true" && website && account) {
      fetchScopesTabData(website, account);
    }
    // eslint-disable-next-line
  }, []);

  const isAnyLoaderTrue =
    isScopesLoading ||
    newScopeLoading ||
    campaignToScopeLoading ||
    editScopeLoading ||
    removeNegativeLoadingForBothLoading ||
    removeNegativeLoadingCampaignLoading;

  return (
    <Paper
      elevation={1}
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        padding: "8px 16px",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={12}>
          <Box>
            <Typography variant="h5">Search Term Operations</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Maintain Clean Campaign Structures and Spot Account-Level Issues
              by Grouping Campaigns into Scopes and Surfacing Negative Search
              Term Anomalies.
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={5.5} md={5.5}>
          <Autocomplete
            data-tracker="494"
            disablePortal
            sx={{ width: "100%" }}
            disabled={isAnyLoaderTrue}
            options={siteOptions}
            getOptionLabel={(option: any) => option?.site_name || ""}
            value={
              siteOptions?.find(
                (option: any) =>
                  option?.shared_uid === selectedSTOperationSharedUid
              ) || null
            }
            onChange={handleSitesChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Website"
                size="small"
                color="secondary"
                placeholder="Select a website from the list"
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={5.5} md={5.5}>
          <Autocomplete
            data-tracker="495"
            disablePortal
            sx={{ width: "100%" }}
            disabled={
              !selectedSTOperationSharedUid ||
              accountsLoading ||
              isAnyLoaderTrue
            }
            options={accountsForSTOperation}
            getOptionLabel={(option: any) => option?.account_name || ""}
            value={
              accountsForSTOperation?.find(
                (option: any) =>
                  option?.account_shared_uid === selectedSTOperationAccount
              ) || null
            }
            onChange={(_, newValue) => {
              if (newValue?.account_shared_uid !== selectedSTOperationAccount) {
                clearParamsForAccountSelection(); // Clear parameters and related states
                if (newValue) {
                  dispatch(
                    setSelectedSTOperationAccount(newValue.account_shared_uid)
                  );
                } else {
                  dispatch(setSelectedSTOperationAccount(""));
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Account"
                size="small"
                color="secondary"
                placeholder={
                  accountsLoading
                    ? "Loading..."
                    : "Select an account from the list"
                }
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {accountsLoading ? (
                        <CircularProgress color="secondary" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={1} md={1} sx={{ alignItems: "right", display: "flex" }}>
          <Button
            data-tracker="496"
            variant="contained"
            color="secondary"
            disabled={
              !selectedSTOperationSharedUid ||
              !selectedSTOperationAccount ||
              isAnyLoaderTrue
            }
            onClick={handleGetDataSTO}
            sx={{ minHeight: 42, ml: 2, width: "100%" }}
          >
            GET DATA
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SearchTermOperationCard;
